import Axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";

import { gatewayTimeout } from "./interceptors/gateway-timeout";
import { redirect } from "./interceptors/request-redirect";
import { retry, type RetryConfig } from "./interceptors/request-retry";
import { transientRetry } from "./interceptors/transient-retry";

export {
  createTokenGate,
  type TokenGate,
  type TokenGateOptions,
} from "./interceptors/token-gate";

declare module "axios" {
  interface AxiosRequestConfig {
    retry?: RetryConfig;
    doNotRetry?: boolean;
    // Narrower flag: skip only the catch-401 session-refresh retry, while the
    // transient network retry stays active. Set on requests replayed after a
    // token refresh so a blip on the replay is still retried.
    doNotAuthRetry?: boolean;
    _transientRetryAttempt?: number;
    originalConfig?: AxiosRequestConfig;
  }
}

type CreateAxiosInstanceResult = {
  msAxiosInstance: <T>(
    config: AxiosRequestConfig,
    options?: AxiosRequestConfig,
  ) => Promise<{
    headers: unknown;
    body: T;
  }>;
  MSAxiosInstance: AxiosInstance;
};

export function createAxiosInstance(): CreateAxiosInstanceResult {
  const MSAxiosInstance = Axios.create();

  // Add a second `options` argument here if you want to pass extra options to each generated query
  const msAxiosInstance = <T>(
    config: AxiosRequestConfig,
    options?: AxiosRequestConfig,
  ): Promise<{ headers: unknown; body: T }> => {
    const source = Axios.CancelToken.source();

    const requestConfig = {
      ...config,
      ...options,
      cancelToken: source.token,
    };

    // oxlint-disable-next-line mobsuccess/member-expressions
    const promise = MSAxiosInstance({
      ...requestConfig,
      originalConfig: requestConfig,
      // oxlint-disable-next-line promise/prefer-await-to-then
    }).then(({ headers, data }) => ({
      headers,
      body: data,
    }));

    Object.defineProperties(promise, {
      cancel: {
        value: () => {
          source.cancel("Query was cancelled");
        },
        writable: false,
      },
    });

    return promise;
  };

  MSAxiosInstance.interceptors.response.use(
    undefined,
    gatewayTimeout.error.bind(MSAxiosInstance),
  );
  MSAxiosInstance.interceptors.response.use(
    redirect.response.bind(MSAxiosInstance),
  );
  MSAxiosInstance.interceptors.request.use(retry.request.bind(MSAxiosInstance));
  MSAxiosInstance.interceptors.response.use(
    retry.response.bind(MSAxiosInstance),
    retry.error.bind(MSAxiosInstance),
  );
  // Vitest mocks 5xx responses on purpose (error-path tests); retrying them
  // only adds delay and stderr noise, so the transient retry stays off there.
  // Webapp builds define `process.env` as `{}`, so vitest's MODE=test is the
  // only signal that survives the vite transform; the optional chain keeps
  // non-vite consumers (node scripts, backend) working.
  const isVitest = import.meta.env?.MODE === "test";
  if (!isVitest) {
    MSAxiosInstance.interceptors.response.use(
      undefined,
      transientRetry.error.bind(MSAxiosInstance),
    );
  }

  return {
    msAxiosInstance,
    MSAxiosInstance,
  };
}
